import { gsap } from "gsap";

const BODY_OPEN_CLASS = 'modal-open';
const cache = new Map();

function buildModal() {
    const { theme_url: themeUrl = '' } = window.my_ajax_object ?? {};
    const closeIconUrl = `${themeUrl}/assets/images/close-x.svg`;

    const el = document.createElement('div');
    el.classList.add('modal');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Modal');
    el.innerHTML = `
        <div class="modal__content">
            <div class="modal__head">
                <button type="button" class="modal__close" data-modal-close aria-label="Cerrar">
                    <span class="modal__close__label">Cerrar</span>
                    <img class="modal__close__icon" src="${closeIconUrl}" alt="" aria-hidden="true">
                </button>
            </div>
            <div class="modal__body" data-lenis-prevent>
                <div class="modal__wrapper" aria-live="polite"></div>
            </div>
        </div>
    `;
    return el;
}

function getFocusable(el) {
    return Array.from(
        el.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
    );
}

function reinitCF7(container) {
    if (typeof wpcf7 === 'undefined') return;
    container.querySelectorAll('.wpcf7 > form').forEach(form => {
        if (typeof wpcf7.init === 'function') wpcf7.init(form);
    });
}

async function fetchFreshNonce(ajaxUrl) {
    const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: new URLSearchParams({ action: 'get_bskr_nonce' }),
    });
    if (!response.ok) return null;

    const json = await response.json();
    return json.success ? json.data.nonce : null;
}

async function loadModal(modalId) {
    if (cache.has(modalId)) return cache.get(modalId);

    const { ajax_url: ajaxUrl } = window.my_ajax_object ?? {};
    if (!ajaxUrl) {
        console.error('Modal: my_ajax_object is not defined.');
        return null;
    }

    // Fetched on demand rather than reading window.my_ajax_object.nonce:
    // that value is baked into the page HTML, which WP Rocket page-caches,
    // so it goes stale once the nonce expires. admin-ajax.php is never
    // page-cached, so this always returns a valid nonce.
    const nonce = await fetchFreshNonce(ajaxUrl);
    if (!nonce) return null;

    const body = new URLSearchParams({
        action:   'get_modal',
        nonce,
        modal_id: modalId,
    });

    const response = await fetch(ajaxUrl, { method: 'POST', body });
    if (!response.ok) return null;

    const json = await response.json();
    if (!json.success) return null;

    cache.set(modalId, json.data.html);
    return json.data.html;
}

async function handleTriggerClick(trigger) {
    const modalId = trigger.dataset.modalTrigger;
    if (!modalId) return;

    const modalEl = buildModal();
    const wrapperEl = modalEl.querySelector('.modal__wrapper');
    const closeBtn  = modalEl.querySelector('[data-modal-close]');

    wrapperEl.innerHTML = '<div class="modal__loading" aria-label="Cargando…" role="status"></div>';

    let isClosing = false;
    let keydownListener;

    const close = () => {
        if (isClosing) return;
        isClosing = true;
        document.removeEventListener('keydown', keydownListener);

        gsap.to(wrapperEl, { opacity: 0, scale: 0.95, y: 20, duration: 0.3, ease: 'power2.in' });
        gsap.to(modalEl, {
            opacity: 0,
            duration: 0.35,
            ease: 'power2.in',
            onComplete: () => {
                if (modalEl.parentNode) document.body.removeChild(modalEl);
                document.body.classList.remove(BODY_OPEN_CLASS);
                window.lenisInstance?.start();
                trigger?.focus();
            },
        });
    };

    keydownListener = e => {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        const focusable = getFocusable(modalEl);
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey) {
            if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
            if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
    };

    closeBtn.addEventListener('click', close);
    modalEl.addEventListener('wheel',     e => e.stopPropagation(), { passive: true });
    modalEl.addEventListener('touchmove', e => e.stopPropagation(), { passive: true });
    modalEl.addEventListener('click', e => {
        if (!e.target.closest('.modal__wrapper') && !e.target.closest('[data-modal-close]')) {
            close();
        }
    });
    document.addEventListener('keydown', keydownListener);

    document.body.appendChild(modalEl);
    document.body.classList.add(BODY_OPEN_CLASS);
    window.lenisInstance?.stop();

    gsap.set(modalEl,   { opacity: 0 });
    gsap.set(wrapperEl, { opacity: 0, scale: 0.95, y: 20 });
    gsap.to(modalEl,   { opacity: 1, duration: 0.35, ease: 'power2.out' });
    gsap.to(wrapperEl, { opacity: 1, scale: 1, y: 0, duration: 0.5, delay: 0.05, ease: 'expo.out' });

    closeBtn.focus();

    const html = await loadModal(modalId);
    if (!modalEl.parentNode) return;

    if (html === null) {
        wrapperEl.innerHTML = '<p class="modal__error">No se pudo cargar el contenido.</p>';
        return;
    }

    wrapperEl.innerHTML = html;
    reinitCF7(wrapperEl);

    const firstFocusable = getFocusable(wrapperEl)[0];
    if (firstFocusable) firstFocusable.focus();
}

export function initModals() {
    document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-modal-trigger]');
        if (trigger && !document.querySelector('.modal')) {
            e.preventDefault();
            handleTriggerClick(trigger);
        }
    });
}
